import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { websocketService, WebSocketMessage } from '../../services/websocketService';
import { AppThunk } from '../store';
import { whatsappApi } from '../services/whatsappApi';

interface Notification {
  id: string;
  contactId: number;
  messageId: number;
  timestamp: string;
  isOfflineSync: boolean;
  messageType?: string;
  hasMedia?: boolean;
}

interface MessagesState {
  notifications: Notification[];
  unreadCounts: Record<number, number>; // Map of contactId -> count
  lastSeenTimestamps: Record<number, string>; // Map of contactId -> timestamp
}

const initialState: MessagesState = {
  notifications: [],
  unreadCounts: {},
  lastSeenTimestamps: {}
};

// Helper function to refetch messages for a specific contact
const refetchMessages = (contactId: number) => {
  // This is just a placeholder - the actual refetch happens via invalidateTags
  console.log(`Refetching messages for contact ${contactId}`);
};

// Initialize WebSocket connection
export const initializeWebSocket = (token: string): AppThunk => (dispatch) => {
  // Add auth headers to WebSocket
  const originalFetch = window.fetch;
  window.fetch = function(input, init) {
    // Only add headers for WebSocket handshake
    if (typeof input === 'string' && input.includes('/ws')) {
      init = init || {};
      init.headers = {
        ...init.headers,
        'Authorization': `Bearer ${token}`
      };
    }
    return originalFetch(input, init);
  };
  
  // Connect to WebSocket
  websocketService.connect(token);
  
  // Add message handler
  websocketService.addMessageHandler((data: WebSocketMessage) => {
    if (data.type === 'new_message') {
      // Only handle incoming messages
      if (data.contact_id) {
        // Invalidate the cache to trigger a refetch
        dispatch(whatsappApi.util.invalidateTags(['Messages']));
        // Refresh the specific contact's messages
        if (data.contact_id) {
          refetchMessages(data.contact_id);
        }
        
        // Generate a unique ID for the notification
        const notificationId = `${data.contact_id}-${data.message_id || 0}-${Date.now()}`;
        
        // Dispatch an action to show a notification
        dispatch(newMessageReceived({
          id: notificationId,
          contactId: data.contact_id,
          messageId: data.message_id || 0,
          timestamp: data.timestamp || new Date().toISOString(),
          isOfflineSync: !!data.offline_sync,
          messageType: data.message_type,
          hasMedia: data.has_media
        }));
      }
    } else if (data.type === 'status_update') {
      // Handle status updates
      dispatch(whatsappApi.util.invalidateTags(['Messages']));
    }
  });
};

const messagesSlice = createSlice({
  name: 'messages',
  initialState,
  reducers: {
    newMessageReceived(state, action: PayloadAction<Notification>) {
      const { contactId } = action.payload;
      // We already have the ID in the payload now
      const notification = { ...action.payload };
      
      
      // Add to notifications (limit to 100 most recent)
      state.notifications = [notification, ...state.notifications.slice(0, 99)];
      
      // Update unread count
      if (!state.unreadCounts[contactId]) {
        state.unreadCounts[contactId] = 0;
      }
      state.unreadCounts[contactId]++;
      
      // Play sound for real-time messages (not offline sync)
      // This would be handled in a middleware or effect
    },
    clearNotifications(state) {
      state.notifications = [];
    },
    markContactRead(state, action: PayloadAction<number>) {
      const contactId = action.payload;
      state.unreadCounts[contactId] = 0;
      state.lastSeenTimestamps[contactId] = new Date().toISOString();
    },
    removeNotification(state, action: PayloadAction<string>) {
      state.notifications = state.notifications.filter(n => n.id !== action.payload);
    }
  }
});

export const { 
  newMessageReceived, 
  clearNotifications, 
  markContactRead,
  removeNotification
} = messagesSlice.actions;

export default messagesSlice.reducer;
